import { Skeleton } from "antd";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTSection } from "@btnet/core-components/BTSection";
import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import {
    useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2,
    useEntityRecordsLinkedEntitiesBySourceExternalIdPutV2,
} from "@btnet/services-btnet/handlers/entity-records/entity-records";
import type { EntityRecordResponse } from "@btnet/services-btnet/handlers/models/entityRecordResponse";
import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import type { PlanPage } from "@btnet/services-btnet/handlers/models/planPage";
import type { PlanPageEntityRecordResponse } from "@btnet/services-btnet/handlers/models/planPageEntityRecordResponse";
import type { SpecificationMappingData } from "@btnet/services-btnet/handlers/models/specificationMappingData";
import type { SpecificationResponse } from "@btnet/services-btnet/handlers/models/specificationResponse";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    BdsButtonGroup,
    BdsIconArrowLeft,
    BdsIconArrowSquareOut,
    BdsIconFlag,
    BdsIconPlans,
    BdsIconSpecifications,
    BdsIconX,
    BdsLayout,
    BdsRollupButton,
} from "@buildertrend/components";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { routes } from "utilities/routes";
import { useRouteProps } from "utilities/url/url";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { EmptyState } from "commonComponents/entity/emptyState/EmptyState";
import { EntityLinkRoutes } from "commonComponents/entity/entityLinkSelector/EntityLinkRoutes";
import {
    addToAvailableEntitiesCache,
    createRelatedItemTagPropsFromEntities,
    type IEntityLinkTagProps,
    removeFromAvailableEntitiesCache,
    updateItemInAvailableEntitiesCache,
    updateLinkedEntitiesCache,
} from "commonComponents/entity/entityLinkSelector/EntityLinkSelector.utilities";
import { RelatedItemTag } from "commonComponents/entity/relatedItem/RelatedItemTag/RelatedItemTag";
import FeedbackEmbeddableIcon from "commonComponents/feedback/FeedbackEmbeddableIcon/FeedbackEmbeddableIcon";

import { RouteBidPackage } from "entity/bidPackage/BidPackage/BidPackageRoute";
import { getEntityNameFromEntityType } from "entity/clientUpdate/clientUpdateUtils";
import { RouteCostGroup } from "entity/CostGroup/CostGroupRoute";
import { DownloadPlanSetIconDropdown } from "entity/Plans/DownloadPlanSetButton/DownloadPlanSetIconDropdown";
import { LinkedPlanSetViewer } from "entity/Plans/LinkedPlanSetViewer/LinkedPlanSetViewer";
import { RoutePurchaseOrder } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrderRoute";
import { RouteSelectionOption } from "entity/selectionOption/SelectionOptionRoute";
import {
    type ISpecificationViewerRef,
    SpecificationViewer,
} from "entity/specification/book/components/SpecificationViewer";
import { arePlansLinkingSupportedForEntity } from "entity/specification/common/SpecificationsPanel/SpecificationsPanelLayout.utilities";
import { SpecificationLinkSelectorV2 } from "entity/specification/SpecificationLinkSelectorV2/SpecificationLinkSelectorV2";
import { toEntityRecord } from "entity/specification/SpecificationLinkSelectorV2/SpecificationLinkSelectorV2.utilities";

import "./SpecificationsPanelLayout.less";

import { SpecificationStatusBadge } from "../SpecificationStatus";
import {
    type ISelectedPlanSet,
    type ISelectedSpec,
    LinkingPanelTab,
} from "./SpecificationsPanelLayout.types";

export interface ISpecificationsPanelLayoutProps {
    /**
     * The external ID of the source entity
     */
    externalId: string;
    /**
     * The entity type of the source entity
     */
    sourceEntityType: EntityType;
    /**
     * The entityId of the source entity
     */
    sourceEntityId?: number;
    /**
     * The job ID for filtering specifications
     */
    jobId: number;
    /**
     * Whether the user can edit (add/remove) specifications.
     * Generally this should just be the same as the current user's edit permission on the source entity and not tied to the source entity state.
     */
    canEdit: boolean;
    /**
     * Callback to close the panel
     */
    onClose: () => void;
    /**
     * Additional class name applied to the select dropdown overlay.
     * Useful for setting z-index when inside a drawer.
     */
    dropdownClassName?: string;
    /**
     * Optional subtitle appended to the header as "Linked specs - {subtitle}"
     */
    subtitle?: string;
    /**
     * Optional mapping data for pre-populating new specification content from estimate group data
     */
    specificationMapping?: SpecificationMappingData;
    /**
     * When true, the host page already renders its own `<RoutePurchaseOrder>` and the layout will
     * skip mounting one. Prevents two routes matching the same URL and double-mounting the PO modal
     */
    hostProvidesPurchaseOrderRoute?: boolean;
    /**
     * When true, the panel runs in mass-link mode:
     * - the batch GET for existing links is disabled and no pre-linked items are shown
     * - empty-state messaging is suppressed
     * - persistence is delegated to `onPersistLinksOverride` (no single-source PUT)
     * - Cancel + Save buttons are rendered in the header in place of the close X
     */
    massLinkMode?: boolean;
    /**
     * In mass-link mode, called when the user adds/removes a tag for a given target type.
     * Receives the current full set of selected target external IDs for that type.
     */
    onPersistLinksOverride?: (targetEntityType: EntityType, ids: string[]) => void;
    /**
     * Mass-link mode: callback fired when the user clicks Save in the panel header.
     */
    onSave?: () => void;
    /**
     * Mass-link mode: when true, the Save button shows a loading state and is disabled.
     */
    isSaving?: boolean;
    /** Whether the source lives on the general job (e.g. Cost Group). Hides viewer actions that don't apply there and the spec status badge. */
    isGeneralJob?: boolean;
}

/**
 * Panel layout component for use with BTSplitView
 * Uses same structure as CommentComponent
 */
export const SpecificationsPanelLayout = ({
    externalId,
    sourceEntityType,
    jobId,
    canEdit,
    onClose,
    dropdownClassName,
    subtitle,
    specificationMapping,
    sourceEntityId,
    hostProvidesPurchaseOrderRoute = false,
    massLinkMode = false,
    onPersistLinksOverride,
    onSave,
    isSaving = false,
    isGeneralJob = false,
}: ISpecificationsPanelLayoutProps) => {
    const { match } = useRouteProps();
    const navigate = useNavigate();
    const webviewContext = useWebviewContext();

    const isInWebview = !!webviewContext;
    // Track which spec is being viewed in the panel (null = list state)
    const [selectedSpecExternalId, setSelectedSpecExternalId] = useState<string | null>(null);
    const [selectedPlanSetExternalId, setSelectedPlanSetExternalId] = useState<string | null>(null);
    const [isViewingPlanSet, setIsViewingPlanSet] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Persists the link between source entity and freshly-created spec — fired from
    // handleInlineCreated below since the panel doesn't go through EntityLinkSelectorEdit.
    const { mutate: syncLinks } = useEntityRecordsLinkedEntitiesBySourceExternalIdPutV2({
        mutation: {
            onError: (error) => {
                showApixErrorMessage(error as Error);
            },
        },
    });

    // Track linked counts from EntityLinkSelector callbacks
    const [linkedSpecCount, setLinkedSpecCount] = useState(0);
    const [linkedPlanSetCount, setLinkedPlanSetCount] = useState(0);

    const plansSupported = arePlansLinkingSupportedForEntity(sourceEntityType);

    // Bid id when viewing from a received bid request, so a sub without job access can still download.
    const bidContextId =
        sourceEntityType === EntityType.BidPackage && sourceEntityId ? sourceEntityId : undefined;

    const [currentLinkingTab, setCurrentLinkingTab] = useState(
        plansSupported ? LinkingPanelTab.Plans : LinkingPanelTab.Specs,
    );

    const viewerRef = useRef<ISpecificationViewerRef>(null);

    const batchEntityTypes = useMemo(
        () =>
            plansSupported
                ? [EntityType.Specification, EntityType.PlanSet, EntityType.PlanPage]
                : [EntityType.Specification],
        [plansSupported],
    );

    // Fetch all linked entities in a single batch call, sharing cache with SpecificationsLinkButton.
    // Disabled in mass-link mode — the wrapper starts visually empty regardless of existing per-source links.
    const { data: linkedEntitiesBatchResponse, isLoading: isBatchLoading } =
        useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2(
            externalId,
            { targetEntityTypes: batchEntityTypes },
            { query: { refetchOnMount: false, enabled: !massLinkMode && !!externalId } },
        );

    // In mass-link mode the batch GET is disabled, but the cache for this query key is still
    // populated by the selector's override branch (via `updateLinkedEntitiesCache`). React Query
    // surfaces that cached data through `data` even when the query is disabled, so the user's
    // pending selections render as RelatedItemTags below the selector.
    const linkedSpecs = useMemo(
        () =>
            linkedEntitiesBatchResponse?.data.entitiesByType.find(
                (g) => g.entityType === EntityType.Specification,
            )?.entityRecords ?? [],
        [linkedEntitiesBatchResponse],
    );

    const linkedPlanSets = useMemo(
        () =>
            linkedEntitiesBatchResponse?.data.entitiesByType.find(
                (g) => g.entityType === EntityType.PlanSet,
            )?.entityRecords ?? [],
        [linkedEntitiesBatchResponse],
    );

    const linkedPlanPages = useMemo(
        () =>
            linkedEntitiesBatchResponse?.data.entitiesByType.find(
                (g) => g.entityType === EntityType.PlanPage,
            )?.entityRecords ?? [],
        [linkedEntitiesBatchResponse],
    );

    const linkedPlanPageIdsBySetId = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const page of linkedPlanPages) {
            if (page.entityRecordResponseType !== "planPage") continue;
            const { setId } = page;
            if (!setId) continue;
            const ids = map.get(setId) ?? new Set<string>();
            ids.add(page.id);
            map.set(setId, ids);
        }
        return map;
    }, [linkedPlanPages]);

    // When a PlanSet is unlinked from this source, the backend cascade-deletes its child
    // PlanPage links — but EntityLinkSelectorEdit's optimistic cache update only touches the
    // PlanSet slice. Mirror the cascade here by culling any cached PlanPage whose parent set
    // is no longer in linkedPlanSets, keeping the "Flagged (N)" badges in sync.
    const linkedPlanSetIds = useMemo(
        () => new Set(linkedPlanSets.map((s) => s.id)),
        [linkedPlanSets],
    );

    const orphanedPlanPageIds = useMemo(() => {
        const orphans: string[] = [];
        for (const page of linkedPlanPages) {
            if (page.entityRecordResponseType !== "planPage") continue;
            if (page.setId && !linkedPlanSetIds.has(page.setId)) {
                orphans.push(page.id);
            }
        }
        return orphans;
    }, [linkedPlanPages, linkedPlanSetIds]);

    useEffect(() => {
        if (orphanedPlanPageIds.length === 0) return;
        const orphanSet = new Set(orphanedPlanPageIds);
        const filteredPages = linkedPlanPages.filter((p) => !orphanSet.has(p.id));
        updateLinkedEntitiesCache(externalId, EntityType.PlanPage, filteredPages, batchEntityTypes);
    }, [orphanedPlanPageIds, linkedPlanPages, externalId, batchEntityTypes]);

    const hasLinkedSpecs = linkedSpecs.length > 0;
    const hasLinkedPlanSets = linkedPlanSets.length > 0;

    const modalConfig: IModalConfiguration = {
        beforeClose: () => navigate(-1),
        parentRoute: match.url,
    };

    // Derive selected spec properties from linked entities query data
    const selectedSpec = useMemo<ISelectedSpec | null>(() => {
        if (selectedSpecExternalId == null) return null;
        const spec = linkedSpecs.find((s) => s.id === selectedSpecExternalId);
        if (!spec) return null;
        return {
            specificationId: spec.entityId,
            externalId: spec.id,
            title: spec.title,
        };
    }, [selectedSpecExternalId, linkedSpecs]);

    // Derive selected planSet properties from linked entities query data
    const selectedPlanSet = useMemo<ISelectedPlanSet | null>(() => {
        if (selectedPlanSetExternalId == null) return null;
        const planSet = linkedPlanSets.find((s) => s.id === selectedPlanSetExternalId);
        if (!planSet) return null;
        return {
            externalId: planSet.id,
            title: planSet.title,
            // The PlanSetEntityRecordResponse union member carries hasAnnotations.
            // Other variants don't, but linkedPlanSets is filtered to PlanSet entity-type
            // rows so the field is present at runtime.
            hasAnnotations: extractHasAnnotations(planSet),
        };
    }, [selectedPlanSetExternalId, linkedPlanSets]);

    // Shared delete handler: removes spec from cache and clears viewer if needed
    const handleDeleted = useCallback(
        (deletedExternalId: string) => {
            // Update linked entities cache - remove deleted spec
            const filtered = linkedSpecs.filter((e) => e.id !== deletedExternalId);
            updateLinkedEntitiesCache(
                externalId,
                EntityType.Specification,
                filtered,
                batchEntityTypes,
            );

            // Update available entities cache - remove deleted spec
            removeFromAvailableEntitiesCache(jobId, EntityType.Specification, deletedExternalId);

            if (selectedSpecExternalId === deletedExternalId) {
                setSelectedSpecExternalId(null);
            }
        },
        [externalId, linkedSpecs, selectedSpecExternalId, jobId, batchEntityTypes],
    );

    // Handle spec deleted from the viewer's actions menu
    const handleDeletedFromViewer = useCallback(() => {
        if (!selectedSpec) return;
        handleDeleted(selectedSpec.externalId);
    }, [selectedSpec, handleDeleted]);

    // Handle spec edited — apply optimistic update to linked entities cache
    const handleEdited = useCallback(
        (entity: EntityRecordResponse) => {
            // Update linked entities cache - update edited spec
            const updated = linkedSpecs.map((linkedEntity) =>
                linkedEntity.id === entity.id
                    ? {
                          ...linkedEntity,
                          title: entity.title ?? linkedEntity.title,
                          status: entity.status ?? linkedEntity.status,
                      }
                    : linkedEntity,
            );
            updateLinkedEntitiesCache(
                externalId,
                EntityType.Specification,
                updated,
                batchEntityTypes,
            );

            // Update available entities cache - update edited spec
            updateItemInAvailableEntitiesCache(jobId, EntityType.Specification, entity);
        },
        [externalId, linkedSpecs, jobId, batchEntityTypes],
    );

    // Status badges on related item tags; suppressed for general-job specs, where Draft/Published isn't relevant.
    const renderStatusBadge = useCallback(
        (entity: EntityRecordResponse): ReactNode | undefined => {
            if (isGeneralJob || entity.status == null) {
                return undefined;
            }

            return <SpecificationStatusBadge status={entity.status} />;
        },
        [isGeneralJob],
    );

    // Create tag props from linked specs
    const specsTagProps = useMemo(
        () =>
            createRelatedItemTagPropsFromEntities(
                linkedSpecs,
                EntityType.Specification,
                jobId,
                () => {}, // onRemove handled by SpecificationLinkSelectorV2
                false, // canRemove is false - removing happens via selector
                renderStatusBadge,
            ),
        [linkedSpecs, jobId, renderStatusBadge],
    );

    // Create tag props from linked planSets
    const planSetTagProps = useMemo(
        () =>
            createRelatedItemTagPropsFromEntities(
                linkedPlanSets,
                EntityType.PlanSet,
                jobId,
                () => {}, // onRemove handled by SpecificationLinkSelectorV2
                false, // canRemove is false - removing happens via selector
                renderStatusBadge,
            ),
        [linkedPlanSets, jobId, renderStatusBadge],
    );

    // Handle spec data changes from the viewer (publish, unpublish, viewing permissions)
    const handleViewerUpdated = useCallback(
        (spec: SpecificationResponse) => {
            handleEdited(toEntityRecord(spec));
        },
        [handleEdited],
    );

    const handleBack = useCallback(() => {
        const clearSelection = () => {
            setSelectedSpecExternalId(null);
            // Leave selectedPlanSetExternalId intact so the LinkedPlanSetViewer instance
            // stays alive (hidden) and its filter state survives a back-out + re-enter.
            setIsViewingPlanSet(false);
            setIsCreating(false);
        };
        if (viewerRef.current) {
            viewerRef.current.guardNavigation(clearSelection);
        } else {
            clearSelection();
        }
    }, []);

    const handleClose = useCallback(() => {
        const closeAndClear = () => {
            setIsCreating(false);
            onClose();
        };
        if (viewerRef.current) {
            viewerRef.current.guardNavigation(closeAndClear);
        } else {
            closeAndClear();
        }
    }, [onClose]);

    const handleCreateInline = useCallback(() => {
        // Mutually exclusive with the other viewer slots.
        setSelectedSpecExternalId(null);
        setSelectedPlanSetExternalId(null);
        setIsCreating(true);
    }, []);

    const handleCancelCreate = useCallback(() => {
        setIsCreating(false);
    }, []);

    const handleInlineCreated = useCallback(
        (spec: SpecificationResponse) => {
            const newRecord = toEntityRecord(spec);

            // Optimistic cache writes — panel + colocated buttons reflect the link before the
            // network round-trip.
            const updatedLinked = [newRecord, ...linkedSpecs];
            updateLinkedEntitiesCache(
                externalId,
                EntityType.Specification,
                updatedLinked,
                batchEntityTypes,
            );
            addToAvailableEntitiesCache(jobId, EntityType.Specification, newRecord);

            // Persist the link between source entity and new spec.
            syncLinks({
                sourceExternalId: externalId,
                data: {
                    sourceEntityType,
                    targetLinks: [
                        {
                            targetEntityType: EntityType.Specification,
                            targetExternalIds: updatedLinked.map((e) => e.id),
                        },
                    ],
                },
                params: { jobParam: jobId },
            });

            // Switch the panel to view the just-created spec. The viewer already toasted the
            // create/publish success, so no extra message here.
            setIsCreating(false);
            setSelectedSpecExternalId(newRecord.id);
        },
        [externalId, sourceEntityType, linkedSpecs, batchEntityTypes, jobId, syncLinks],
    );

    // Factory function to create click handlers for spec tags
    const createSpecClickHandler = useCallback(
        (tag: IEntityLinkTagProps) => () => {
            setSelectedSpecExternalId(tag.externalId);
        },
        [],
    );

    // Factory function to create click handlers for plan tags
    const createPlanSetClickHandler = useCallback(
        (tag: IEntityLinkTagProps) => () => {
            setSelectedPlanSetExternalId(tag.externalId);
            setIsViewingPlanSet(true);
        },
        [],
    );

    const canLinkPlanPages = canEdit && plansSupported && !massLinkMode;

    const selectedPlanSetLinkedPageIds = useMemo(
        () =>
            selectedPlanSet
                ? (linkedPlanPageIdsBySetId.get(selectedPlanSet.externalId) ?? new Set<string>())
                : new Set<string>(),
        [selectedPlanSet, linkedPlanPageIdsBySetId],
    );

    const handleTogglePlanPageLink = useCallback(
        (plan: PlanPage) => {
            if (!canLinkPlanPages) return;
            const pageId = plan.pageId;
            if (!pageId) return;

            const currentlyLinked = linkedPlanPages.some((p) => p.id === pageId);
            const nextLinkedPages = currentlyLinked
                ? linkedPlanPages.filter((p) => p.id !== pageId)
                : [
                      ...linkedPlanPages,
                      {
                          entityRecordResponseType: "planPage",
                          id: pageId,
                          entityType: EntityType.PlanPage,
                          entityId: 0,
                          organizationId: 0,
                          title: plan.fileName,
                          status: null,
                          isDeleted: false,
                          jobId,
                          setId: plan.setId,
                      } as PlanPageEntityRecordResponse,
                  ];

            updateLinkedEntitiesCache(
                externalId,
                EntityType.PlanPage,
                nextLinkedPages,
                batchEntityTypes,
            );

            syncLinks({
                sourceExternalId: externalId,
                data: {
                    sourceEntityType,
                    targetLinks: [
                        {
                            targetEntityType: EntityType.PlanPage,
                            targetExternalIds: nextLinkedPages.map((p) => p.id),
                        },
                    ],
                },
                params: { jobParam: jobId },
            });
        },
        [
            batchEntityTypes,
            canLinkPlanPages,
            externalId,
            jobId,
            linkedPlanPages,
            sourceEntityType,
            syncLinks,
        ],
    );

    const handleTabChangeToPlans = useCallback(
        () => setCurrentLinkingTab(LinkingPanelTab.Plans),
        [],
    );
    const handleTabChangeToSpecs = useCallback(
        () => setCurrentLinkingTab(LinkingPanelTab.Specs),
        [],
    );

    const isViewingSpec = selectedSpec != null;
    const linkedPlanSetCountText = linkedPlanSetCount ? `Plans (${linkedPlanSetCount})` : "Plans";
    const linkedSpecCountText = linkedSpecCount ? `Specs (${linkedSpecCount})` : "Specs";
    const specsHeader = "Linked specs";
    const panelHeaderText = plansSupported ? "Link Plans and Specs" : specsHeader;

    const handleOpenPlanSetInNewTab = () => {
        window.open(
            `${routes.appBase}${routes.plans.getPlansListLink(selectedPlanSet?.externalId)}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    // Hosts that can't supply a source id yet (unsaved entity, catalog-scoped option) have
    // nothing to link against — the batch query above is disabled for the same reason.
    if (!externalId) {
        return null;
    }

    // Single BdsLayout ensures the split view only sees one layout child, preventing
    // drag-line desync. The list content section stays mounted (hidden via CSS)
    // so EntityLinkSelector hooks remain active, preventing React Query
    // refetchOnMount from overwriting optimistic cache updates.
    return (
        <>
            <BdsLayout
                displayType="HCF"
                id="specifications-panel"
                testid="specifications-panel"
                useMaxWidth
                contentAlign="compact"
                className={cn("SpecificationsPanelLayout", {
                    "SpecificationsPanelLayout--viewing-plan-set": isViewingPlanSet,
                })}
            >
                {/* Header - the plan-set viewer header or the list header. While viewing/creating
                    a spec, SpecificationViewer renders its own header, so this renders nothing. */}
                {!isViewingSpec && !isCreating && (
                    <BTSection
                        id="specifications-panel-header"
                        data-testid="specifications-panel-header"
                        className="block"
                    >
                        {isViewingPlanSet ? (
                            <div className="flex flex-row justify-between items-center">
                                <div className="flex flex-row items-center SpecificationsPanelViewerTitle">
                                    <BTButton
                                        aria-label="Back"
                                        type="tertiary"
                                        icon={<BdsIconArrowLeft />}
                                        onClick={handleBack}
                                        data-testid="specifications-panel-back"
                                    />
                                    <BTPopover
                                        content={selectedPlanSet?.title}
                                        data-testid="specifications-panel-title-tooltip"
                                    >
                                        <span className="SpecificationsPanelViewerTitleText">
                                            <BTText
                                                text={selectedPlanSet?.title}
                                                size="distinct-lg"
                                                className="SpecificationsPanelViewerTitleTruncate"
                                            />
                                        </span>
                                    </BTPopover>
                                </div>
                                <div className="flex flex-row">
                                    {!isInWebview && (
                                        <BTButton
                                            aria-label="Open in new tab"
                                            type="tertiary"
                                            icon={<BdsIconArrowSquareOut />}
                                            onClick={handleOpenPlanSetInNewTab}
                                            data-testid="open-plan-set-new-tab"
                                        />
                                    )}
                                    {selectedPlanSet && (
                                        <DownloadPlanSetIconDropdown
                                            setId={selectedPlanSet.externalId}
                                            jobId={jobId}
                                            setHasAnnotations={selectedPlanSet.hasAnnotations}
                                            bidId={bidContextId}
                                        />
                                    )}
                                    {!massLinkMode && (
                                        <BTButton
                                            aria-label="Close"
                                            type="tertiary"
                                            icon={<BdsIconX />}
                                            onClick={handleClose}
                                            data-testid="close-specifications-panel"
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-row justify-between items-center">
                                <div className="flex flex-col">
                                    <div className="flex flex-row SpecificationsPanelTitleRow items-center">
                                        {!plansSupported && <BdsIconSpecifications size="lg" />}
                                        <BTText text={panelHeaderText} size="distinct-lg" />
                                    </div>
                                    {subtitle && !plansSupported && (
                                        <BTText
                                            text={subtitle}
                                            size="normal-md"
                                            className="SpecificationsPanelSubtitle"
                                        />
                                    )}
                                </div>
                                <div className="flex flex-row">
                                    {!massLinkMode && (
                                        <FeedbackEmbeddableIcon
                                            feedbackTarget={
                                                currentLinkingTab === LinkingPanelTab.Plans
                                                    ? FeedbackTarget.Plans
                                                    : FeedbackTarget.Specifications
                                            }
                                            extraInfo={JSON.stringify({
                                                sourceEntityName:
                                                    getEntityNameFromEntityType(sourceEntityType),
                                                sourceEntityType,
                                                jobId,
                                            })}
                                        />
                                    )}
                                    {massLinkMode && (
                                        <span style={{ display: "inline-flex", gap: 8 }}>
                                            <BTButton
                                                type="secondary"
                                                onClick={onClose}
                                                data-testid="mass-link-cancel"
                                            >
                                                Cancel
                                            </BTButton>
                                            <BTButton
                                                type="primary"
                                                disabled={
                                                    linkedSpecCount + linkedPlanSetCount === 0
                                                }
                                                loading={isSaving}
                                                onClick={onSave}
                                                data-testid="mass-link-save"
                                            >
                                                Save
                                            </BTButton>
                                        </span>
                                    )}
                                    {!massLinkMode && (
                                        <BTButton
                                            aria-label="Close"
                                            type="tertiary"
                                            icon={<BdsIconX />}
                                            id="close-specifications-panel"
                                            onClick={onClose}
                                            data-testid="close-specifications-panel"
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </BTSection>
                )}

                <BTSection
                    id="specifications-panel-content"
                    data-testid="specifications-panel-content"
                    className={cn({
                        "p-0 h-full": isViewingSpec || isCreating,
                    })}
                >
                    {/* Viewer - shown when viewing a spec */}
                    {isViewingSpec && (
                        <SpecificationViewer
                            specificationId={selectedSpec.specificationId}
                            externalId={selectedSpec.externalId}
                            jobId={jobId}
                            bidId={bidContextId}
                            onUpdated={handleViewerUpdated}
                            onDelete={handleDeletedFromViewer}
                            onBack={handleBack}
                            ref={viewerRef}
                            mode="linkedDrawer"
                            isGeneralJob={isGeneralJob}
                        />
                    )}

                    {/* Viewer - shown when inline-creating a new spec */}
                    {isCreating && (
                        <SpecificationViewer
                            specificationId={null}
                            jobId={jobId}
                            onBack={handleBack}
                            ref={viewerRef}
                            mode="linkedDrawer"
                            isCreating
                            isGeneralJob={isGeneralJob}
                            specificationMapping={specificationMapping}
                            onCreated={handleInlineCreated}
                            onCancelCreate={handleCancelCreate}
                        />
                    )}

                    {selectedPlanSet && (
                        <div style={{ display: isViewingPlanSet ? undefined : "none" }}>
                            <LinkedPlanSetViewer
                                key={selectedPlanSet.externalId}
                                jobId={jobId}
                                setId={selectedPlanSet.externalId}
                                sourceEntityId={sourceEntityId}
                                sourceEntityType={sourceEntityType}
                                canLinkPlanPages={canLinkPlanPages}
                                linkedPlanPageIds={selectedPlanSetLinkedPageIds}
                                onTogglePlanPageLink={handleTogglePlanPageLink}
                            />
                        </div>
                    )}

                    {/* List content - hidden when viewer is active */}
                    <div
                        className={cn("SpecificationsPanelBody", {
                            "SpecificationsPanelBody--hidden":
                                isViewingSpec || isViewingPlanSet || isCreating,
                        })}
                    >
                        {plansSupported && (
                            <BdsButtonGroup
                                id="specsPlansToggle"
                                testid="specs-plans-toggle"
                                compact
                                stages={1}
                                menuTestid="specsPlansToggle-menu"
                                rollupTestid="specsPlansToggleRollup"
                                selectable
                                flexAlign="start"
                                className={canEdit ? undefined : "margin-bottom-md"}
                            >
                                <BdsRollupButton
                                    id="plansViewSelectButton"
                                    testid="view-toggle-plans"
                                    onClick={handleTabChangeToPlans}
                                    selected={currentLinkingTab === LinkingPanelTab.Plans}
                                    text={linkedPlanSetCountText}
                                    value="plans"
                                    minStage={1}
                                />
                                <BdsRollupButton
                                    id="specsViewSelectButton"
                                    testid="view-toggle-specs"
                                    onClick={handleTabChangeToSpecs}
                                    selected={currentLinkingTab === LinkingPanelTab.Specs}
                                    text={linkedSpecCountText}
                                    value="specifications"
                                    minStage={1}
                                />
                            </BdsButtonGroup>
                        )}

                        {/* Specification Link Selector - only shown when user can edit */}
                        {canEdit ? (
                            <div className="SpecificationSelectorContainer">
                                <SpecificationLinkSelectorV2
                                    sourceExternalId={externalId}
                                    sourceEntityType={sourceEntityType}
                                    jobId={jobId}
                                    isReadOnly={false}
                                    canAdd={canEdit && !massLinkMode}
                                    canEdit={false}
                                    canRemove={canEdit}
                                    dropdownClassName={dropdownClassName}
                                    specificationMapping={specificationMapping}
                                    showPlanSets={!!plansSupported}
                                    onSpecLinkedCountChange={setLinkedSpecCount}
                                    onPlanSetLinkedCountChange={setLinkedPlanSetCount}
                                    currentLinkingTab={currentLinkingTab}
                                    queryKeyEntityTypes={batchEntityTypes}
                                    onCreateInline={!massLinkMode ? handleCreateInline : undefined}
                                    onPersistLinksOverride={onPersistLinksOverride}
                                    isGeneralJob={isGeneralJob}
                                />
                            </div>
                        ) : (
                            // In view-only mode, we still need routes to handle spec modal opening
                            <EntityLinkRoutes onDeleted={handleDeleted} onEdited={handleEdited} />
                        )}

                        {/* Loading State */}
                        {((isBatchLoading && currentLinkingTab === LinkingPanelTab.Specs) ||
                            (isBatchLoading && currentLinkingTab === LinkingPanelTab.Plans)) && (
                            <div className="SpecificationSkeletonColumn">
                                <Skeleton.Input active />
                                <Skeleton.Input active />
                            </div>
                        )}

                        {/* Empty Specs State — suppressed in mass-link mode */}
                        {!massLinkMode &&
                            !isBatchLoading &&
                            !hasLinkedSpecs &&
                            currentLinkingTab === LinkingPanelTab.Specs && (
                                <EmptyState
                                    title="No linked Specs"
                                    image={
                                        <div className="EmptyStateIcon">
                                            <BdsIconSpecifications size="2xl" />
                                        </div>
                                    }
                                    data-testid="specifications-empty-state"
                                >
                                    Link specs to define requirements and keep everyone aligned
                                </EmptyState>
                            )}

                        {/* Empty Plans State — suppressed in mass-link mode */}
                        {!massLinkMode &&
                            !isBatchLoading &&
                            !hasLinkedPlanSets &&
                            currentLinkingTab === LinkingPanelTab.Plans && (
                                <EmptyState
                                    title="No linked Plans"
                                    image={
                                        <div className="EmptyStateIcon">
                                            <BdsIconPlans size="2xl" />
                                        </div>
                                    }
                                    data-testid="plans-empty-state"
                                >
                                    Link plans to provide visual context and keep everyone aligned
                                </EmptyState>
                            )}

                        {/* Linked Specifications Display */}
                        {!isBatchLoading &&
                            hasLinkedSpecs &&
                            currentLinkingTab === LinkingPanelTab.Specs && (
                                <div className="LinkedSpecificationsColumn">
                                    {specsTagProps.map((props) => (
                                        <RelatedItemTag
                                            key={props.externalId}
                                            data-testid={`related-specification-${props.externalId}`}
                                            subject={props.subject}
                                            itemType={props.itemType}
                                            useMultiTagMargins
                                            hideEntityTypeLabel
                                            status={props.status}
                                            onAction={createSpecClickHandler(props)}
                                            shouldPreventNavigation
                                        />
                                    ))}
                                </div>
                            )}
                        {!isBatchLoading &&
                            hasLinkedPlanSets &&
                            currentLinkingTab === LinkingPanelTab.Plans && (
                                <div className="LinkedSpecificationsColumn">
                                    {planSetTagProps.map((props) => {
                                        // Look back at the originating entity record to pull
                                        // hasAnnotations off the typed PlanSetEntityRecordResponse
                                        // (createRelatedItemTagPropsFromEntities doesn't propagate
                                        // entity-type-specific fields into the tag props shape).
                                        const sourceRecord = linkedPlanSets.find(
                                            (s) => s.id === props.externalId,
                                        );
                                        const pageCount =
                                            linkedPlanPageIdsBySetId.get(props.externalId)?.size ??
                                            0;

                                        return (
                                            <RelatedItemTag
                                                key={props.externalId}
                                                className="LinkedPlanSetTag"
                                                data-testid={`related-plan-set-${props.externalId}`}
                                                subject={props.subject}
                                                itemType={props.itemType}
                                                useMultiTagMargins
                                                hideEntityTypeLabel
                                                status={props.status}
                                                onAction={createPlanSetClickHandler(props)}
                                                shouldPreventNavigation
                                                actions={
                                                    !massLinkMode ? (
                                                        <div className="flex flex-row RelatedItemActions">
                                                            {pageCount > 0 && (
                                                                <BTStatusBadge
                                                                    testid={`linkedPlanPageCount-${props.externalId}`}
                                                                    displayType="danger"
                                                                    icon={<BdsIconFlag />}
                                                                    ariaLabel="Linked Plan Page count"
                                                                    text={`${pageCount}`}
                                                                />
                                                            )}
                                                            <DownloadPlanSetIconDropdown
                                                                setId={props.externalId}
                                                                jobId={jobId}
                                                                displayType="secondary"
                                                                setHasAnnotations={
                                                                    sourceRecord
                                                                        ? extractHasAnnotations(
                                                                              sourceRecord,
                                                                          )
                                                                        : false
                                                                }
                                                                bidId={bidContextId}
                                                            />
                                                        </div>
                                                    ) : undefined
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            )}
                    </div>
                </BTSection>
            </BdsLayout>
            <RouteBidPackage isFromSpecification modalConfig={modalConfig} />
            <RouteCostGroup modalConfig={modalConfig} />
            {!hostProvidesPurchaseOrderRoute && <RoutePurchaseOrder modalConfig={modalConfig} />}
            <RouteSelectionOption modalConfig={modalConfig} />
        </>
    );
};

// Linked-entity rows for plan sets come back as PlanSetEntityRecordResponse, which is the
// only union member carrying hasAnnotations. Narrow via the discriminator so the field
// access stays type-safe regardless of variant.
function extractHasAnnotations(
    record: { entityRecordResponseType?: string } & {
        hasAnnotations?: boolean;
    },
): boolean {
    return record.entityRecordResponseType === "planSet" && record.hasAnnotations === true;
}
